import { NetworkStatus } from '@apollo/client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITruncateString from 'UIComponents/text/UITruncateString';
import { useFilteredEnrollmentsList } from 'automation-ui-canvas/containers/enrollment-history/panel/hooks/successfulEnrollmentsHooks';
import { ENROLLMENT_HISTORY_LIST_FIELDNAME } from 'automation-ui-canvas/containers/enrollment-history/panel/queries/successfulEnrollmentsQueries';
import { getEnrollmentHistoryUrl } from 'automation-ui-canvas/utils/urls';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import ObjectNameReference from 'automation-ui-enrollment/components/filters/enrollment/generic-enrollment-table/ObjectNameReference';
import FormattedDateTimeWithZones from 'automation-ui-i18n/FormattedDateTimeWithZones';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ENROLLMENT_SELECT_PAGE_SIZE = 50;
const EnrollmentOptionComponent = ({
  option,
  children
}) => {
  if (option.isSpecial) {
    return children;
  }
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    className: "p-all-4",
    children: [/*#__PURE__*/_jsx(UITruncateString, {
      children: /*#__PURE__*/_jsx(ObjectNameReference, {
        id: option.enrollmentInfo.objectId,
        objectTypeId: option.enrollmentInfo.objectTypeId,
        disableTruncateString: false,
        href: null
      })
    }), /*#__PURE__*/_jsx("small", {
      children: /*#__PURE__*/_jsx(FormattedDateTimeWithZones, {
        value: option.enrollmentInfo.enrollmentTimestamp,
        format: "lll z"
      })
    })]
  });
};
const EnrollmentSelect = ({
  flowId,
  flowObjectTypeId,
  isHybrid,
  currentEnrollmentLogEvent
}) => {
  var _searchParams$get$spl, _searchParams$get, _paging$next;
  const [seenEnrollmentIds, setSeenEnrollmentIds] = useState([]);
  const trackEvent = useTrackEvent();
  const history = useHistory();
  const {
    search
  } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const stringActionIdsList = (_searchParams$get$spl = (_searchParams$get = searchParams.get('actions')) === null || _searchParams$get === void 0 ? void 0 : _searchParams$get.split(',')) !== null && _searchParams$get$spl !== void 0 ? _searchParams$get$spl : [];
  const reachedActionIds = stringActionIdsList.map(stringActionIds => parseInt(stringActionIds, 10));
  const {
    data: {
      [ENROLLMENT_HISTORY_LIST_FIELDNAME]: enrollmentsResponse = {}
    } = {},
    loading,
    error,
    networkStatus,
    fetchMore
  } = useFilteredEnrollmentsList({
    flowId,
    objectTypeId: flowObjectTypeId,
    hybrid: !!isHybrid,
    skip: !flowObjectTypeId,
    reachedActionIds,
    seenEnrollmentIds
  });
  const {
    results: enrollmentsList = [],
    paging
  } = enrollmentsResponse;
  const hasMore = !!(paging !== null && paging !== void 0 && paging.next);
  const offset = paging === null || paging === void 0 || (_paging$next = paging.next) === null || _paging$next === void 0 ? void 0 : _paging$next.after;
  useEffect(() => {
    if (reachedActionIds.length > 0 && enrollmentsList.length !== seenEnrollmentIds.length) {
      setSeenEnrollmentIds(enrollmentsList.map(enrollment => enrollment.enrollmentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentsList]);
  const options = enrollmentsList.map(enrollmentInfo => ({
    text: String(enrollmentInfo.enrollmentId),
    value: String(enrollmentInfo.enrollmentId),
    enrollmentInfo
  }));
  const loadOptions = useCallback((__, callback, pagination = {
    optionsOffset: 0
  }) => {
    if (pagination.optionsOffset !== 0) {
      fetchMore({
        variables: {
          offset
        }
      }).then(({
        data: {
          [ENROLLMENT_HISTORY_LIST_FIELDNAME]: {
            results,
            paging: fetchMorePaging
          }
        }
      }) => {
        const hasMoreResults = !!(fetchMorePaging !== null && fetchMorePaging !== void 0 && fetchMorePaging.next);
        const nextOptions = [...options, ...results.map(enrollmentInfo => ({
          text: String(enrollmentInfo.enrollmentId),
          value: String(enrollmentInfo.enrollmentId),
          enrollmentInfo
        }))];
        callback(null, {
          options: nextOptions,
          pagination: {
            hasMore: hasMoreResults,
            length: nextOptions.length,
            optionsOffset: pagination.optionsOffset + ENROLLMENT_SELECT_PAGE_SIZE
          }
        });
      }).catch(err => {
        reportError({
          error: err
        });
      });
    } else {
      callback(null, {
        options,
        pagination: {
          hasMore,
          length: options.length,
          optionsOffset: pagination.optionsOffset + ENROLLMENT_SELECT_PAGE_SIZE
        }
      });
    }
  }, [fetchMore, hasMore, offset, options]);
  if (loading && networkStatus !== NetworkStatus.fetchMore) {
    return /*#__PURE__*/_jsx(SkeletonText, {
      width: 200
    });
  }
  if (error) {
    reportError({
      error
    });
  }
  const handleEnrollmentClick = enrollmentId => {
    searchParams.set('enrollmentId', enrollmentId.toString());
    history.replace({
      pathname: getEnrollmentHistoryUrl({
        flowId
      }),
      search: searchParams.toString()
    });
  };
  const EnrollmentItemComponent = () => {
    return /*#__PURE__*/_jsx(ObjectNameReference, {
      id: currentEnrollmentLogEvent === null || currentEnrollmentLogEvent === void 0 ? void 0 : currentEnrollmentLogEvent.objectId,
      objectTypeId: currentEnrollmentLogEvent.objectTypeId,
      disableTruncateString: false,
      href: null
    });
  };
  return /*#__PURE__*/_jsx(UISelect, {
    buttonUse: "form-on-dark",
    optionComponent: EnrollmentOptionComponent,
    itemComponent: EnrollmentItemComponent,
    loadMoreComponent: /*#__PURE__*/_jsx(UILink, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.enrollmentHistory.panel.selected-enrollment.loadMore"
      })
    }),
    onChange: evt => {
      trackEvent(Trackables.PERFORMANCE_HISTORY_CHANGED_ENROLLMENT_SELECT);
      handleEnrollmentClick(evt.target.value);
    },
    loadOptions: loadOptions,
    searchable: false,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.enrollmentHistory.panel.selected-enrollment.selectAnEnrollment"
    }),
    className: "m-bottom-2",
    value: searchParams.get('enrollmentId')
  });
};
export default EnrollmentSelect;